const text = value => typeof value === 'string' && value.trim() ? value.trim() : null;
const finite = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
const freeze = value => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

// Explicit whitelist for a future patient-facing report. No fetch, formulas,
// current-patient anthropometry, free-text notes, AI context or reference values.
// Numeric presence is not clinical validation: finite saved zeros/negatives are
// retained as recorded, never repaired or silently converted into another value.
export function buildBiaReportSnapshot(patient, measurement) {
  if (!text(patient?.id) || !text(measurement?.id) || measurement?.patientId !== patient.id) {
    throw new Error('Impossibile preparare il referto: misura salvata e paziente non corrispondono.');
  }
  const measuredAt = text(measurement.measuredAt);
  if (!measuredAt || !Number.isFinite(Date.parse(measuredAt))) {
    throw new Error('Impossibile preparare il referto: data della misura assente o non valida.');
  }
  const missingOrInvalid = [];
  const reading = (value, key) => {
    const result = finite(value);
    if (result === null) missingOrInvalid.push(key);
    return result;
  };
  const contextKnown = ['patient-at-acquisition-1', 'gedip-original-context-1'].includes(measurement.patientContextVersion);
  const context = {
    version: text(measurement.patientContextVersion),
    status: contextKnown ? 'recorded' : 'unavailable',
    heightCm: contextKnown ? reading(measurement.heightCmAtMeasurement, 'context.heightCm') : null,
    ageYears: contextKnown ? reading(measurement.ageYearsAtMeasurement, 'context.ageYears') : null,
    sex: contextKnown ? text(measurement.sexAtMeasurement) : null,
    bodyBuild: contextKnown ? text(measurement.bodyBuildAtMeasurement) : null,
    waistCm: contextKnown ? reading(measurement.waistCmAtMeasurement, 'context.waistCm') : null,
    hipCm: contextKnown ? reading(measurement.hipCmAtMeasurement, 'context.hipCm') : null
  };
  const composition = {};
  for (const key of ['bmi', 'fm', 'fM_pct', 'ffm', 'mm', 'bcm', 'bcmi', 'tbw', 'ecw', 'icw', 'bmr', 'phaseAngle50']) {
    composition[key] = reading(measurement.processingIssue ? null : measurement.bodyComposition?.[key], `composition.${key}`);
  }
  return freeze({
    version: 'bia-report-snapshot-1',
    measurementId: measurement.id,
    patient: { id: patient.id, firstName: text(patient.firstName), lastName: text(patient.lastName), identitySource: 'current-patient-record' },
    measuredAt,
    formulaVersion: text(measurement.formulaVersion),
    acquisitionProfile: text(measurement.acquisitionProfile),
    calculationAgeYears: finite(measurement.calculationAgeYears),
    processingIssue: text(measurement.processingIssue),
    weightKg: reading(measurement.weightKg, 'weightKg'),
    context,
    composition,
    frequencies: [5, 50, 100].map(khz => ({
      khz,
      resistance: reading(measurement[`resistance${khz}`], `raw.resistance${khz}`),
      reactance: reading(measurement[`reactance${khz}`], `raw.reactance${khz}`),
      impedance: reading(measurement[`impedance${khz}`], `raw.impedance${khz}`),
      phase: reading(measurement[`phase${khz}`], `raw.phase${khz}`)
    })),
    missingOrInvalid,
    includesReferenceCalculations: false,
    isClinicallyValidated: false
  });
}
